// src/pages/CadastrarPremio.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Box,
  CheckCircle2,
  Gift,
  ImagePlus,
  Package,
  Save,
  Truck,
  Zap,
} from "lucide-react";
import "./CadastrarPremio.css";

function CadastrarPremio() {
  const navigate = useNavigate();

  const [valorPonto, setValorPonto] = useState(0.03);
  const [pontosSugeridos, setPontosSugeridos] = useState(0);
  const [valorPontosEditado, setValorPontosEditado] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categorias, setCategorias] = useState([]);

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    categoria: "Todos",
    preco_unitario: "",
    estoque: "",
    valor_em_pontos: "",
    valor_em_pontos_promocional: "",
    promocao_inicio: "",
    promocao_fim: "",
    imagem: null,
    peso_kg: "",
    comprimento_cm: "",
    largura_cm: "",
    altura_cm: "",
    frete_gratis: false,
    exige_voltagem: false,
    voltagens_disponiveis: [],
  });

  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    const carregarValorPonto = async () => {
      try {
        const res = await axios.get("/configuracoes/valor-ponto");
        setValorPonto(res.data.valor || 0.03);
      } catch (err) {
        console.error("Erro ao buscar valor do ponto:", err);
      }
    };

    const carregarCategorias = async () => {
      try {
        const res = await axios.get("/categorias-premios");
        setCategorias((res.data || []).filter((c) => c.ativa));
      } catch (err) {
        console.error("Erro ao carregar categorias:", err);
      }
    };

    carregarValorPonto();
    carregarCategorias();
  }, []);

  const avisoFreteIncompleto = useMemo(
    () =>
      ["peso_kg", "comprimento_cm", "largura_cm", "altura_cm"].some(
        (campo) => !formData[campo]
      ),
    [formData]
  );

  const calcularPontos = (preco) => {
    const valorPago = parseFloat(preco || 0);
    const valorPontoAtual = parseFloat(valorPonto || 0.03);
    const pontos = Math.ceil((valorPago * 1.2) / valorPontoAtual);

    setPontosSugeridos(pontos);

    if (!valorPontosEditado) {
      setFormData((prev) => ({ ...prev, valor_em_pontos: pontos }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "preco_unitario") calcularPontos(value);
    if (name === "valor_em_pontos") setValorPontosEditado(true);
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, imagem: e.target.files[0] }));
  };

  const handleVoltagemChange = (voltagem, checked) => {
    setFormData((prev) => {
      const atuais = prev.voltagens_disponiveis || [];

      return {
        ...prev,
        voltagens_disponiveis: checked
          ? [...atuais, voltagem]
          : atuais.filter((item) => item !== voltagem),
      };
    });
  };

  const limparFormulario = () => {
    setFormData({
      nome: "",
      descricao: "",
      categoria: "Todos",
      preco_unitario: "",
      estoque: "",
      valor_em_pontos: "",
      valor_em_pontos_promocional: "",
      promocao_inicio: "",
      promocao_fim: "",
      imagem: null,
      peso_kg: "",
      comprimento_cm: "",
      largura_cm: "",
      altura_cm: "",
      frete_gratis: false,
      exige_voltagem: false,
      voltagens_disponiveis: [],
    });

    setPontosSugeridos(0);
    setValorPontosEditado(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErro("");
    setMensagem("");
    setIsSubmitting(true);

    if (!formData.imagem) {
      setErro("Por favor, selecione uma imagem.");
      setIsSubmitting(false);
      return;
    }

    const data = new FormData();

    for (const campo in formData) {
      if (campo !== "imagem") {
        if (campo === "voltagens_disponiveis") {
          data.append(campo, (formData[campo] || []).join(","));
        } else {
          data.append(campo, formData[campo]);
        }
      }
    }

    if (formData.imagem) {
      data.append("imagem", formData.imagem);
    }

    try {
      await axios.post("/premios", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMensagem("Prêmio cadastrado com sucesso!");
      limparFormulario();

      setTimeout(() => navigate("/premios"), 2000);
    } catch (err) {
      console.error("Erro ao cadastrar prêmio:", err);
      setErro(err.response?.data?.error || "Erro ao cadastrar prêmio.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cadastrarPremioAdmin-page">
      <div className="cadastrarPremioAdmin-topbar">
        <div>
          <span className="cadastrarPremioAdmin-kicker">
            Catálogo de recompensas
          </span>

          <h2>Cadastrar Novo Prêmio</h2>

          <p>
            Cadastre o prêmio com imagem, estoque, pontuação, promoção,
            voltagem e dados de frete para cálculo logístico.
          </p>
        </div>

        <Link to="/premios" className="cadastrarPremioAdmin-btn secondary">
          <ArrowLeft size={16} />
          Voltar para prêmios
        </Link>
      </div>

      {mensagem && (
        <div className="cadastrarPremioAdmin-alert success">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      {erro && <div className="cadastrarPremioAdmin-alert danger">{erro}</div>}

      <form className="cadastrarPremioAdmin-form" onSubmit={handleSubmit}>
        <div className="cadastrarPremioAdmin-layout">
          <section className="cadastrarPremioAdmin-card">
            <div className="cadastrarPremioAdmin-cardHeader">
              <div>
                <h3>Dados principais</h3>
                <span>Informações básicas exibidas no catálogo.</span>
              </div>

              <Gift size={18} />
            </div>

            <div className="cadastrarPremioAdmin-grid">
              <div className="cadastrarPremioAdmin-field">
                <label>Nome do prêmio</label>
                <input
                  type="text"
                  name="nome"
                  placeholder="Ex: Smartphone Samsung"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Categoria</label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nome}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cadastrarPremioAdmin-field cadastrarPremioAdmin-fieldFull">
                <label>Descrição</label>
                <textarea
                  name="descricao"
                  placeholder="Descreva o prêmio, regras ou detalhes importantes"
                  value={formData.descricao}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Estoque inicial</label>
                <input
                  type="number"
                  name="estoque"
                  placeholder="Ex: 20"
                  value={formData.estoque}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Imagem do prêmio</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </section>

          <section className="cadastrarPremioAdmin-card">
            <div className="cadastrarPremioAdmin-cardHeader">
              <div>
                <h3>Pontos e promoção</h3>
                <span>Defina o custo em pontos e período promocional.</span>
              </div>

              <Zap size={18} />
            </div>

            <div className="cadastrarPremioAdmin-grid">
              <div className="cadastrarPremioAdmin-field">
                <label>Preço pago por unidade</label>
                <input
                  type="number"
                  step="0.01"
                  name="preco_unitario"
                  placeholder="R$"
                  value={formData.preco_unitario}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-pontosSugeridos">
                <span>Pontos sugeridos</span>
                <strong>{pontosSugeridos.toLocaleString("pt-BR")} pts</strong>
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Pontos necessários</label>
                <input
                  type="number"
                  name="valor_em_pontos"
                  placeholder="Ex: 3500"
                  value={formData.valor_em_pontos}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Pontos promocionais</label>
                <input
                  type="number"
                  name="valor_em_pontos_promocional"
                  placeholder="Opcional"
                  value={formData.valor_em_pontos_promocional}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Início da promoção</label>
                <input
                  type="date"
                  name="promocao_inicio"
                  value={formData.promocao_inicio}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Fim da promoção</label>
                <input
                  type="date"
                  name="promocao_fim"
                  value={formData.promocao_fim}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              {valorPontosEditado &&
                String(formData.valor_em_pontos) !== String(pontosSugeridos) && (
                  <div className="cadastrarPremioAdmin-warning cadastrarPremioAdmin-fieldFull">
                    Você está alterando os pontos sugeridos. Sugerido:{" "}
                    <strong>{pontosSugeridos.toLocaleString("pt-BR")} pts</strong>{" "}
                    / Atual:{" "}
                    <strong>
                      {Number(formData.valor_em_pontos || 0).toLocaleString("pt-BR")} pts
                    </strong>
                  </div>
                )}
            </div>
          </section>

          <section className="cadastrarPremioAdmin-card">
            <div className="cadastrarPremioAdmin-cardHeader">
              <div>
                <h3>Voltagem</h3>
                <span>Configure se o cliente deverá escolher voltagem.</span>
              </div>

              <Box size={18} />
            </div>

            <div className="cadastrarPremioAdmin-cardBody">
              <label className="cadastrarPremioAdmin-toggleBox">
                <input
                  type="checkbox"
                  checked={formData.exige_voltagem}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      exige_voltagem: e.target.checked,
                      voltagens_disponiveis: e.target.checked
                        ? prev.voltagens_disponiveis
                        : [],
                    }))
                  }
                  disabled={isSubmitting}
                />

                <span className="cadastrarPremioAdmin-toggleTrack">
                  <span className="cadastrarPremioAdmin-toggleThumb" />
                </span>

                <span>
                  <strong>Este prêmio exige escolha de voltagem</strong>
                  <small>Use para eletrodomésticos, eletrônicos e similares.</small>
                </span>
              </label>

              {formData.exige_voltagem && (
                <div className="cadastrarPremioAdmin-checkGrid">
                  {["110V", "220V", "Bivolt"].map((voltagem) => (
                    <label key={voltagem} className="cadastrarPremioAdmin-checkItem">
                      <input
                        type="checkbox"
                        checked={formData.voltagens_disponiveis.includes(voltagem)}
                        onChange={(e) =>
                          handleVoltagemChange(voltagem, e.target.checked)
                        }
                        disabled={isSubmitting}
                      />
                      <span>{voltagem}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="cadastrarPremioAdmin-card">
            <div className="cadastrarPremioAdmin-cardHeader">
              <div>
                <h3>Frete e dimensões</h3>
                <span>Dados opcionais para cálculo automático de frete.</span>
              </div>

              <Truck size={18} />
            </div>

            <div className="cadastrarPremioAdmin-grid">
              <div className="cadastrarPremioAdmin-field">
                <label>Peso</label>
                <input
                  type="number"
                  step="0.01"
                  name="peso_kg"
                  placeholder="kg"
                  value={formData.peso_kg}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Comprimento</label>
                <input
                  type="number"
                  name="comprimento_cm"
                  placeholder="cm"
                  value={formData.comprimento_cm}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Largura</label>
                <input
                  type="number"
                  name="largura_cm"
                  placeholder="cm"
                  value={formData.largura_cm}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-field">
                <label>Altura</label>
                <input
                  type="number"
                  name="altura_cm"
                  placeholder="cm"
                  value={formData.altura_cm}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="cadastrarPremioAdmin-fieldFull">
                <label className="cadastrarPremioAdmin-toggleBox">
                  <input
                    type="checkbox"
                    checked={formData.frete_gratis}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        frete_gratis: e.target.checked,
                      }))
                    }
                    disabled={isSubmitting}
                  />

                  <span className="cadastrarPremioAdmin-toggleTrack">
                    <span className="cadastrarPremioAdmin-toggleThumb" />
                  </span>

                  <span>
                    <strong>Frete grátis para este prêmio</strong>
                    <small>Marcado quando o cliente não deve pagar frete.</small>
                  </span>
                </label>
              </div>

              {avisoFreteIncompleto && (
                <div className="cadastrarPremioAdmin-warning cadastrarPremioAdmin-fieldFull">
                  Sem preencher peso e dimensões, o frete não poderá ser calculado
                  automaticamente.
                </div>
              )}
            </div>
          </section>

          <aside className="cadastrarPremioAdmin-card cadastrarPremioAdmin-previewCard">
            <div className="cadastrarPremioAdmin-cardHeader">
              <div>
                <h3>Prévia</h3>
                <span>Confira a imagem antes de cadastrar.</span>
              </div>

              <ImagePlus size={18} />
            </div>

            <div className="cadastrarPremioAdmin-previewBox">
              {formData.imagem ? (
                <img src={URL.createObjectURL(formData.imagem)} alt="Prévia" />
              ) : (
                <div className="cadastrarPremioAdmin-previewEmpty">
                  <Package size={28} />
                  <span>Nenhuma imagem selecionada</span>
                </div>
              )}
            </div>

            <div className="cadastrarPremioAdmin-previewInfo">
              <strong>{formData.nome || "Nome do prêmio"}</strong>
              <span>
                {formData.valor_em_pontos
                  ? `${Number(formData.valor_em_pontos).toLocaleString("pt-BR")} pts`
                  : "Pontuação ainda não definida"}
              </span>
            </div>
          </aside>
        </div>

        <div className="cadastrarPremioAdmin-submitBar">
          <button
            type="submit"
            className="cadastrarPremioAdmin-btn primary"
            disabled={isSubmitting}
          >
            <Save size={16} />
            {isSubmitting ? "Cadastrando..." : "Cadastrar prêmio"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CadastrarPremio;